/**
 * CAMBIAR EL LOGO DEL CLUB.
 *
 * Se podía poner al crear el club y nunca más. Un club que arrancó sin logo
 * —o que se hizo uno nuevo— no tenía forma de cambiarlo sin entrar a la base,
 * y el logo es lo primero que se ve de un club: en su ficha, en la lista de
 * clubes y ahora en la vitrina de torneos.
 *
 * VIVE ADENTRO DE «EDITAR CLUB», y por eso no tiene plegado propio. Lo tuvo
 * mientras estaba suelto en la pantalla —un selector de archivo permanente
 * compite con todo lo que sí se usa todos los días—, pero adentro de una
 * sección que ya está plegada detrás de un botón, dos plegados anidados son
 * dos clics para llegar al mismo lugar.
 *
 * CAMBIAR EL LOGO ES EDITAR EL CLUB, igual que cambiar la descripción o el
 * pueblo. Tenerlo aparte obligaba a adivinar cuál de las dos cosas abrir.
 *
 * QUITARLO ES UNA OPCIÓN DE VERDAD y no un descuido: sin logo, la ficha
 * muestra las iniciales del club, que se ven bien. Un club puede preferir eso
 * a un logo viejo que ya no lo representa.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Community } from '@tennisup/contracts';

import { Alert } from './Alert';
import { ImagePicker } from './ImagePicker';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function LogoBox({
  community,
  onChanged,
}: {
  community: Community;
  onChanged: (community: Community) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [logo, setLogo] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Devuelve `true` solo si salió bien: quien llama limpia únicamente entonces. */
  async function correr(accion: () => Promise<Community>): Promise<boolean> {
    setSending(true);
    setError(null);
    try {
      onChanged(await accion());
      return true;
    } catch (caught) {
      setError(errorMessage(t, caught));
      return false;
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="logo-box">
      {/* SIN TÍTULO PROPIO. Adentro de «Editar club», un «Cambiar el logo del
          club» encima de un campo que ya se llama «Logo» son dos rótulos para
          la misma cosa, y el de arriba no agrega nada. */}
      {error !== null && <Alert kind="error">{error}</Alert>}

      <ImagePicker
        id="community-logo"
        label={t('newCommunity.logo')}
        help={t('communities.logoHelp')}
        currentUrl={assetUrl(community.logoPath)}
        value={logo}
        disabled={sending}
        onChange={setLogo}
      />

      <div className="row-actions">
        <button
          type="button"
          disabled={logo === null || sending}
          onClick={() => {
            if (logo === null) return;
            void correr(() => api.setCommunityLogo(community.id, logo)).then((ok) => {
              // Se limpia SOLO si salió bien: con un error, la imagen elegida
              // se queda para poder reintentar sin volver a buscarla.
              if (ok) setLogo(null);
            });
          }}
        >
          {/* «Guardar el logo» y no «Guardar»: esta caja vive adentro de
              «Editar club», que tiene su propio botón de guardar a un palmo
              de acá. Dos botones con el mismo nombre al lado, guardando cosas
              distintas, son ambiguos mirándolos y directamente
              indistinguibles por lector de pantalla. */}
          {t('communities.saveLogo')}
        </button>

        {community.logoPath !== null && (
          <button
            type="button"
            className="link danger"
            disabled={sending}
            onClick={() => {
              void correr(() => api.removeCommunityLogo(community.id));
            }}
          >
            {t('communities.removeLogo')}
          </button>
        )}

      </div>
    </div>
  );
}
